import isEmpty from 'lodash/isEmpty';
import { dataTestId } from 'modules/experiments/constants';
import {
  useTakeoverExperimentContext,
  useVariantCardContext,
  useVariantConfigurationContext,
} from 'modules/experiments/contexts';
import {
  getTakeoverCampaignMessageMedium,
  getTakeoverExperimentType,
} from 'modules/experiments/ducks/selectors';
import { useExperimentsTranslations } from 'modules/experiments/hooks';
import { SubmitButtonType } from 'modules/experiments/types';
import { getDefaultSendTimeFieldValues, getVariantFormFieldValue } from 'modules/experiments/utils';
import { addNegativeToast } from 'modules/toastContainer';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { BUTTON_SIZES, GhostButton, PrimaryButton, SecondaryButton } from 'ui/button';
import { Add16, Save } from 'ui/icons';
import { Tooltip } from 'ui/tooltip';
import { EditableVariantCardFooterWrapper } from './Styled';

export const EditableCardAction = () => {
  const { t } = useExperimentsTranslations();
  const dispatch = useDispatch();
  const type = useSelector(getTakeoverExperimentType);
  const messageMedium = useSelector(getTakeoverCampaignMessageMedium);
  const [currentSubmitType, setCurrentSubmitType] = useState('' as SubmitButtonType);
  const { isRunningExperiment } = useTakeoverExperimentContext();
  const { hasMaximumNumberOfVariants, addNewVariant, toggleToPreviewSelectMode } =
    useVariantConfigurationContext();
  const {
    isSendTimeExperiment,
    sendTimeMetaInfo: { controlStartTime, sendTimeOfAllVariants, sendTimeStartTimeZone },
    formActions: { saveDirtyVariantCard },
  } = useTakeoverExperimentContext();
  const { updateCard, updatingCard, variant } = useVariantCardContext();
  const {
    formState: { isDirty, errors },
    getValues,
    reset,
    trigger,
    handleSubmit,
  } = useFormContext();
  /**
   * The 'isValid' flag provided by the form context doesn't seem to
   * update as expected. When a user edits the form, the 'isValid' flag
   * is erroneously set to false, even though the error object remains empty.
   * Therefore, it is more reliable to use the error object
   * as the definitive indicator of form validity.
   * @see https://github.com/react-hook-form/react-hook-form/issues/2755
   */
  const isValid = isEmpty(errors);
  const disableSave = !isDirty;

  const onSubmit = async (submitType: SubmitButtonType) => {
    // This will trigger the loading state in the clicked save button
    setCurrentSubmitType(submitType);

    // Don't save the card if there are no new changes.
    // But, create a new variant as requested and short-circuit the event
    if (submitType === SubmitButtonType.SUBMIT_AND_ADD_VARIANT && !isDirty) {
      addNewVariant();
      return;
    }

    const response = await updateCard(getValues(), submitType);
    // Add a new variant only if the current card update passes
    if (submitType === SubmitButtonType.SUBMIT_AND_ADD_VARIANT && response.isSuccess) {
      addNewVariant();
    }

    return response;
  };

  // We want to save any dirty forms whenever
  // dispatchSaveDirtyVariantCard is called
  // And, call the corresponding onSuccess and onError callbacks
  useEffect(() => {
    const saveChanges = async () => {
      if (isDirty && isValid) {
        const response = await onSubmit(SubmitButtonType.SIMPLY_SUBMIT);
        if (response && response.isSuccess) saveDirtyVariantCard.onSuccess();
        if (response && !response.isSuccess) saveDirtyVariantCard.onError();
      } else if (isDirty && !isValid) {
        saveDirtyVariantCard.onError();
        dispatch(addNegativeToast(t('variantCard.errors.validation')));
      }
    };
    saveChanges();
  }, [saveDirtyVariantCard.trigger]);

  return (
    <EditableVariantCardFooterWrapper data-test={dataTestId.variantCardFooterEditable}>
      <GhostButton
        data-test={dataTestId.variantCardFooterCancel}
        isDisabled={updatingCard}
        onClick={() => {
          /**
           * On cancel we will toggle the variant selected from an edit mode to review
           * We will also reset any form change that might have occurred.
           * And, then trigger a form validation to check for any form errors
           */
          const defaultValues = isSendTimeExperiment
            ? getDefaultSendTimeFieldValues({
                variant,
                controlStartTime,
                sendTimeOfAllVariants,
                sendTimeStartTimeZone,
              })
            : getVariantFormFieldValue({ variant, type, messageMedium });
          toggleToPreviewSelectMode();
          reset(defaultValues);
          trigger();
        }}
        size={BUTTON_SIZES.SMALL}
      >
        {t('button.cancel')}
      </GhostButton>
      {!isRunningExperiment && (
        <Tooltip
          isEnabled={hasMaximumNumberOfVariants}
          tooltipContent={t('variantCard.tooltip.disableVariantCreation')}
        >
          <SecondaryButton
            data-test={dataTestId.variantCardFooterSaveAndAddVariant}
            isLoading={currentSubmitType === SubmitButtonType.SUBMIT_AND_ADD_VARIANT}
            isDisabled={updatingCard || hasMaximumNumberOfVariants}
            onClick={handleSubmit(
              () => onSubmit(SubmitButtonType.SUBMIT_AND_ADD_VARIANT),
              () => dispatch(addNegativeToast(t('variantCard.errors.validation'))),
            )}
          >
            <Add16 /> {t('button.saveAndAddVariant')}
          </SecondaryButton>
        </Tooltip>
      )}
      <PrimaryButton
        data-test={dataTestId.variantCardFooterSave}
        isLoading={updatingCard && currentSubmitType === SubmitButtonType.SIMPLY_SUBMIT}
        isDisabled={disableSave || updatingCard}
        onClick={handleSubmit(
          () => onSubmit(SubmitButtonType.SIMPLY_SUBMIT),
          () => dispatch(addNegativeToast(t('variantCard.errors.validation'))),
        )}
      >
        <Save height={16} width={16} /> {t('button.save')}
      </PrimaryButton>
    </EditableVariantCardFooterWrapper>
  );
};
